<template>
  <multiselect
    id="locationSearch" class="location-select multiselect-rlnx" v-model="location" label="name" track-by="id" placeholder="Type to search" open-direction="bottom"
    :options="options" :searchable="true" :loading="$apollo.queries.options.loading" :internal-search="$root.isGWR || $root.isSWR" :close-on-select="true"
    :max-height="600" :show-no-results="false" :hide-selected="false" :preserve-search="true" :show-labels="false" @search-change="onSearch" :multiple="multiple">
    <template v-slot:option="{ option }">
      <div class="option-container">
        <span class="option-title">{{ option.name }}</span>
        <span class="option-description" :class="isRemoveLabel(option) ? 'remove-label' : ''">
          <template v-if="isRemoveLabel(option)">Click to remove</template>
          <template v-else>{{ option.description }}<template v-if="option.countryCode"> {{ toFlag(option.countryCode) }}</template></template>
        </span>
      </div>
    </template>
    <template v-slot:noOptions>
      E.g. London, New York, San Francisco
    </template>
  </multiselect>
</template>

<script>
import flag from 'country-code-emoji';
import Multiselect from 'vue-multiselect';
import LocationsIndexQuery from '@/graphql/queries/LocationsIndex.gql';
import LocationsSuggestQuery from '@/graphql/queries/LocationsSuggest.gql';

export default {
  components: { Multiselect },
  props: {
    value: {
      default() {
        return this.multiple ? [] : {};
      },
    },
    onlyCities: {
      type: Boolean,
      default: () => false,
    },
    multiple: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    location: null,
    searchName: null,
    searchNameTimeout: null,
    options: [],
  }),
  created() {
    if (this.$root.isGWR || this.$root.isSWR) {
      this.location = this.value;
      this.options = GWROptions;
    }
  },
  apollo: {
    onload: {
      query: LocationsIndexQuery,
      variables() {

        if (!this.multiple) {
          const { id } = this.value;
          return { where: { id } };
        }
        // @TODO: ElasticSearch throws an error if id_in is used
        const ids = Array.isArray(this.value) ? this.value.map(({ id }) => ({ id })) : [];
        return { where: { OR: ids } };
      },
      manual: true,
      result({ data: { locations } = {} } = {}) {
        if (!this.multiple && locations.length) {
          this.location = locations[0] || {};
          this.options = [ this.location ];
        } else if (this.multiple && locations.length) {
          this.location = locations;
          this.options = this.location;
          console.log('OPTIONS: ', this.options);
        }
      },
      skip() {
        return this.searchName || !this.hasValue || this.$root.isGWR || this.$root.isSWR;
      },
      fetchPolicy: 'network-only',
    },
    options: {
      query: LocationsSuggestQuery,
      variables() {
        const { where } = this;
        return { where };
      },
      update: ({ locationsSuggest }) => locationsSuggest || [],
      skip() {
        return !this.searchName || this.searchName.length === 0 || this.$root.isGWR || this.$root.isSWR;
      },
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    where() {
      const where = {
        name: this.searchName || '',
        featureCodes: [ 'ADM1', 'ADM2', 'ADM3', 'ADMD', 'RGN', 'PPL', 'PPLS', 'PPLC', 'PPLA', 'PPLA2', 'PPLA3', 'PPLA4', 'PPLA5', 'PPLF', 'PPLX' ],
      };
      if (this.onlyCities) {
        where.featureCodes = [ 'PPL', 'PPLS', 'PPLC', 'PPLA', 'PPLA2', 'PPLA3', 'PPLA4', 'PPLA5', 'PPLF', 'PPLX' ];
      }
      return where;
    },
    hasValue() {
      return this.multiple ? (Array.isArray(this.value) && this.value.length) : this.value;
    },
  },
  methods: {
    onSearch(val) {
      clearTimeout(this.searchNameTimeout);
      this.searchNameTimeout = setTimeout(() => {
        this.searchName = val;
      }, 500);
    },
    toFlag: countryCode => flag(countryCode),
    isRemoveLabel(option) {
      if (!this.multiple) {
        return this.value && option.id === this.value.id;
      } else if (this.multiple && Array.isArray(this.value)) {
        return this.value.map(({ id }) => id).includes(option.id);
      }
      return false;
    }
  },
  watch: {
    location() {
      if (!this.multiple) {
        const { id: existingID } = this.value || {};
        const { id: newID } = this.location || {};
        if (existingID !== newID) {
          this.$emit('input', this.location);
        }
      } else {
        const existingIDs = Array.isArray(this.value) ? this.value.map(({ id }) => id) : [];
        const newIDs = Array.isArray(this.location) ? this.location.map(({ id }) => id) : [];
        if (JSON.stringify(existingIDs) !== JSON.stringify(newIDs)) {
          this.$emit('input', this.location);
        }
      }
    },
    value() {
      console.log('VALUE FROM WATCHER: ', this.value);
      if (this.$root.isGWR || this.$root.isSWR) {
        console.log('VALUE FROM WATCHER: ', this.value);
        this.location = this.value;
      }
    },
  },
};

export const GWROptions = [
  { name: 'South East', id: 'South East' },
  { name: 'South West', id: 'South West' },
  { name: 'London', id: 'London' },
  { name: 'East', id: 'East' },
  { name: 'Wales', id: 'Wales' },
  { name: 'International', id: 'International' }
];
</script>

<style lang="scss">
.location-select {
  .option-container {
    .option-title,
    .option-description {
      display: block;
    }
    .option-title {
      color: #333;
      font-size: 14px;
    }
    .option-description {
      color: #666;
      font-size: 12px;

      &.remove-label {
        color: #d63031;
        font-weight: bold;
      }
    }
  }
  .multiselect__placeholder {
    margin: 0 !important;
    padding: 0 !important;
    color: #6c757d;
  }
  .multiselect__single {
    font-size: 1rem !important;
    line-height: 1.5;
    margin: 0 !important;
    padding: 0 !important;
  }
  .multiselect__select {
    display: none !important;
  }
  #locationSearch {
    font-size: 1rem !important;
    line-height: 1.5;
    margin: 0 !important;
    padding: 0 !important;
  }
  .multiselect__option--highlight {
    background: #f2f2f2;
    outline: none;
  }
  &.multiselect-rlnx {
    .multiselect__tags {
      height: auto;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/misc/LocationSearch.vue